import React, { useRef, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import {
  ChevronLeft,
  ChevronRight,
  User,
  Cloud,
  Smartphone,
  Check,
  Brain,
  Layout,
  Cpu,
  Monitor,
} from 'lucide-react';

import { motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';
import { useNavigate } from 'react-router-dom';

const serviceRoutes = {
  "Generative AI Solutions": "/services/gen-ai",
  "Mobile App Development": "/services/mobile-app-development",
  "Web Development": "/services/web-development",
  "DevOps Consulting": "/services/devops-consulting",
  "Salesforce Development": "/services/Salesforce-services",
};

const services = [
  {
    id: 1,
    icon: <Brain className="w-10 h-10" style={{ color: '#f7941d' }} />,
    title: "Generative AI Solutions",
    features: [
      "Custom AI model development & deployment",
      "LLM fine-tuning for business needs",
      "Prompt engineering and chatbot integration",
      "AI-based automation & workflow optimization",
    ],
  },
  {
    id: 2,
    icon: <Smartphone className="w-10 h-10" style={{ color: '#f7941d' }} />,
    title: "Mobile App Development",
    features: [
      "Custom mobile application development",
      "Mobile app design and development",
      "Cross-platform compatibility",
      "User experience (UX) and user interface (UI) design",
    ],
  },
  {
    id: 3,
    icon: <Layout className="w-10 h-10" style={{ color: '#f7941d' }} />,
    title: "Web Development",
    features: [
      "Full-stack web development",
      "Frontend UI/UX engineering",
      "Responsive design & accessibility",
      "API integrations & backend systems",
    ],
  },
  {
    id: 4,
    icon: <User className="w-10 h-10" style={{ color: '#f7941d' }} />,
    title: "DevOps Consulting",
    features: [
      "Cloud infrastructure assessment and migration",
      "Continuous integration and deployment (CI/CD)",
      "Scalability and performance optimization",
      "DevOps automation and monitoring",
    ],
  },
  {
    id: 5,
    icon: <Cloud className="w-10 h-10" style={{ color: '#f7941d' }} />,
    title: "Salesforce Development",
    features: [
      "Salesforce customization and configuration",
      "CRM integration and implementation",
      "Sales and service cloud solutions",
      "Salesforce training and support",
    ],
  },
];

const Services = () => {
  const swiperRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      if (isMobile) {
        swiperRef.current.swiper.autoplay.stop();
      } else {
        swiperRef.current.swiper.autoplay.start();
      }
    }
  }, [isMobile]);

  const goNext = () => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const goPrev = () => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  return (
    <section className="pt-16 px-4 relative" id="services" style={{ backgroundColor: '#F9FAFB' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          whileInView={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5 }}
          className="text-center md:text-left mb-12"
        >
          <p className="font-semibold text-sm uppercase tracking-wider mb-4 text-[#f7941d]">
            We Love Our Work
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Our Services</h2>
          <div className="w-16 h-1 mt-4 mx-auto md:mx-0 bg-[#f7941d]"></div>
        </motion.div>

        {/* Swiper Carousel with Side Navigation and decorative icons */}
        <div className="relative">
          {/* Decorative left icon */}
          <Cpu
            className="absolute left-[-60px] top-1/2 transform -translate-y-1/2 text-orange-200 opacity-30 w-12 h-12 pointer-events-none"
            aria-hidden="true"
          />

          {/* Left Arrow */}
          <button
            onClick={goPrev}
            className="absolute left-[-20px] top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-orange-500 hover:text-orange-500 transition-colors duration-300 bg-white shadow-md"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Swiper Container */}
          <div
            className="w-full"
            onMouseEnter={() => {
              if (!isMobile && swiperRef.current?.swiper?.autoplay) {
                swiperRef.current.swiper.autoplay.stop();
              }
            }}
            onMouseLeave={() => {
              if (!isMobile && swiperRef.current?.swiper?.autoplay) {
                swiperRef.current.swiper.autoplay.start();
              }
            }}
          >
            <Swiper
              ref={swiperRef}
              modules={[Navigation, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              autoplay={{
                delay: 1500,
                disableOnInteraction: false,
              }}
              speed={700}
              loop={true}
              centeredSlides={false}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="services-swiper px-4"
            >
              {services.map((service) => (
                <SwiperSlide key={service.id} className="h-full">
                  <motion.div
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="group bg-white rounded-xl duration-200 p-4 min-h-[380px] max-h-[380px] flex flex-col mx-auto"
                  >
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-full bg-orange-50">{service.icon}</div>
                    </div>
                    <h3 className="text-lg font-bold text-center text-gray-900 mb-3">{service.title}</h3>
                    <ul className="space-y-2 flex-1 overflow-y-auto">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <Check className="w-4 h-4 mt-0.5 text-orange-500 flex-shrink-0" />
                          <span className="text-gray-600 text-sm leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button
                        className="w-full bg-[#f7941d] hover:bg-orange-500 text-white font-semibold py-2 px-4 rounded text-sm"
                        onClick={() => navigate(serviceRoutes[service.title])}
                      >
                        Learn More
                      </button>
                    </div>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Right Arrow */}
          <button
            onClick={goNext}
            className="absolute right-[-40px] top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-orange-500 hover:text-orange-500 transition-colors duration-300 bg-white shadow-md"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Decorative right icon */}
          <Monitor
            className="absolute right-[-60px] top-1/2 transform -translate-y-1/2 text-orange-200 opacity-30 w-12 h-12 pointer-events-none"
            aria-hidden="true"
          />
        </div>
      </div>
    </section>
  );
};

export default Services;
